import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from '../index'

// ==================== WiFi Status ====================

export interface WifiStatusResponse {
    enabled: number
    connected: number
    state: number
    ssid: string
    ipAddress: string
    // Extended fields (LTCB220 v26.4.0+ sub-opcode 100; absent on LTSP101)
    hasSavedNetwork?: number
    savedSsid?: string
    rssi?: number
    lastErrorReason?: number
    lastErrorMsg?: string
}

function httpError(message: string, status: number): Error & { status?: number } {
    const error: Error & { status?: number } = new Error(message)
    error.status = status
    return error
}

export async function getWifiStatus(clientId: string): Promise<WifiStatusResponse> {
    const response: Response = await fetch(`${HOST_URL}wifi/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw httpError(`HTTP Error ${response.status}`, response.status)
    }

    const json: ApiResponse<WifiStatusResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw httpError(json.message, response.status)
    }

    return json.data
}

// ==================== WiFi Saved Config (SSID + Password) ====================

/** Device's saved station config (base opcode 8 — works on all firmware). */
export interface WifiConfigResponse {
    staSsid: string
    staPass: string
}

export async function getWifiConfig(clientId: string): Promise<WifiConfigResponse> {
    const response: Response = await fetch(`${HOST_URL}wifi/config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw httpError(`HTTP Error ${response.status}`, response.status)
    }

    const json: ApiResponse<WifiConfigResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw httpError(json.message, response.status)
    }

    return json.data
}

// ==================== WiFi Enable ====================

export interface WifiEnableResponse {
    success: number
    errorMsg?: string
}

export async function enableWifi(clientId: string, enable: boolean): Promise<WifiEnableResponse> {
    const response: Response = await fetch(`${HOST_URL}wifi/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, enable: enable ? 1 : 0 })
    })

    if (!response.ok) {
        throw httpError(`HTTP Error ${response.status}`, response.status)
    }

    const json: ApiResponse<WifiEnableResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw httpError(json.message, response.status)
    }

    if (json.data.success === 0) {
        throw new Error(json.data.errorMsg || 'WiFi operation failed')
    }

    return json.data
}

// ==================== WiFi Disable ====================

export async function disableWifi(clientId: string): Promise<WifiEnableResponse> {
    const response: Response = await fetch(`${HOST_URL}wifi/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw httpError(`HTTP Error ${response.status}`, response.status)
    }

    const json: ApiResponse<WifiEnableResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw httpError(json.message, response.status)
    }

    if (json.data.success === 0) {
        throw new Error(json.data.errorMsg || 'WiFi disable failed')
    }

    return json.data
}

// ==================== WiFi Connect ====================

export interface WifiConnectResponse {
    success: number // 1 = ACCEPTED (not "connected"), 0 = rejected
    errorMsg?: string
    state?: number // WifiState at response time (0..3)
}

export async function connectWifi(clientId: string, ssid: string, password: string): Promise<WifiConnectResponse> {
    const response: Response = await fetch(`${HOST_URL}wifi/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ssid, password })
    })

    if (!response.ok) {
        throw httpError(`HTTP Error ${response.status}`, response.status)
    }

    const json: ApiResponse<WifiConnectResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw httpError(json.message, response.status)
    }

    if (json.data.success === 0) {
        throw new Error(json.data.errorMsg || 'WiFi connect failed')
    }

    return json.data
}

// ==================== WiFi Forget ====================

export interface WifiForgetResponse {
    success: number
    errorMsg?: string
}

export async function forgetWifi(clientId: string): Promise<WifiForgetResponse> {
    const response: Response = await fetch(`${HOST_URL}wifi/forget`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId })
    })

    if (!response.ok) {
        throw httpError(`HTTP Error ${response.status}`, response.status)
    }

    const json: ApiResponse<WifiForgetResponse> = await response.json()
    if (response.status !== HttpStatus.Ok) {
        throw httpError(json.message, response.status)
    }

    if (json.data.success === 0) {
        throw new Error(json.data.errorMsg || 'WiFi forget failed')
    }

    return json.data
}
